import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { CustomAlarmsListContext } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-list/contexts/custom-alarms-list.context';
import { CustomAlarmListItem } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-list/settings-devices-custom-alarm-list.model';
import { useDeviceTypes } from 'core/contexts/device-types/hooks/use-device-types.hook';
import { getCustomAlarmDefinitions } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-list/service/custom-alarm-list.service';
import { useParams } from 'react-router-dom';
import { showErrorNotification } from 'core/utils/notification-service';
import { useTranslation } from 'core/i18n';
import { mapCustomAlarmDefinitionToListItem } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-list/hooks/use-custom-alarms-list/custom-alarms-list.mapper';

interface CustomAlarmsListProviderProps extends PropsWithChildren {
    enabled?: boolean;
}

export const CustomAlarmsListProvider: React.FC<CustomAlarmsListProviderProps> = (props) => {
    const { enabled = true } = props;
    const { getDeviceTypeDetailsBySlug } = useDeviceTypes();
    const { id } = useParams();
    const { t } = useTranslation();

    const [selectedRows, setSelectedRows] = useState<CustomAlarmListItem[]>([]);
    const [customAlarms, setCustomAlarms] = useState<CustomAlarmListItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [onlyActive, setOnlyActive] = useState<boolean>(true);

    const deviceType = getDeviceTypeDetailsBySlug(id ?? '');

    const fetchList = useCallback(() => {
        if (!deviceType?.id || !enabled) {
            return;
        }

        setIsLoading(true);
        getCustomAlarmDefinitions(deviceType.id, onlyActive)
            .then((definitions) =>
                setCustomAlarms(definitions.map((def) => mapCustomAlarmDefinitionToListItem(def, t)))
            )
            .catch(() => showErrorNotification(t('settings.devices.custom-alarms.list.error')))
            .finally(() => setIsLoading(false));
    }, [deviceType?.id, enabled, onlyActive, t]);

    const handleOnlyActiveChange = useCallback((newValue: boolean) => {
        setOnlyActive(newValue);
        setSelectedRows([]);
    }, []);

    // Reset the selection whenever the device type or the enabled flag changes.
    useEffect(() => {
        setSelectedRows([]);
    }, [deviceType?.id, enabled]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const value = useMemo(
        () => ({
            selectedRows,
            customAlarms,
            isLoading,
            setCustomAlarms,
            setSelectedRows,
            setIsLoading,
            fetchList,
            onlyActive,
            handleOnlyActiveChange,
        }),
        [customAlarms, fetchList, isLoading, onlyActive, selectedRows, handleOnlyActiveChange]
    );

    return <CustomAlarmsListContext.Provider value={value}>{props.children}</CustomAlarmsListContext.Provider>;
};
